import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  ReferenceLine,
} from "recharts";
import SideNavLayout from "../../Sidenav/SideNavLayout";
import {
  EMR_HISTORY,
  COMPANY_STATS,
  COMPANY_KEYS,
  buildMetricByYear,
} from "./hseStatsData";

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 ${className}`}
  >
    {children}
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="text-left mb-3">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
  </div>
);

const KpiTile = ({ label, value, color, hint }) => (
  <div
    className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-left"
    style={{ borderLeft: `4px solid ${color}` }}
  >
    <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="mt-1 text-2xl font-bold text-[#0A0A0A]">{value}</div>
    {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

const METRICS = [
  { key: "trir",       label: "TRIR (Total Recordable Incident Rate)", subtitle: "Lower is better" },
  { key: "recordable", label: "Recordable Injuries / Illnesses",       subtitle: "Total count per year" },
  { key: "lostTime",   label: "Lost-Time & Days-Away Cases",           subtitle: "Severity indicator" },
  { key: "fatalities", label: "Fatalities",                            subtitle: "Total count per year" },
  { key: "hours",      label: "Total Hours Worked",                    subtitle: "Exposure base" },
  { key: "employees",  label: "Average Number of Employees",           subtitle: "Headcount" },
];

const HseStatsDashboard = () => {
  const [activeMetric, setActiveMetric] = useState("trir");
  const [selectedCompany, setSelectedCompany] = useState("");

  const metricData = useMemo(() => buildMetricByYear(activeMetric), [activeMetric]);

  // Career totals — fall back to combined Operations when no company is picked
  const fallbackKey = selectedCompany || "OPERATIONS";
  const selectedCompanyCfg = COMPANY_STATS[fallbackKey];
  const companySeries = selectedCompanyCfg.series;
  const totalRecordable = companySeries.reduce((a, r) => a + (r.recordable || 0), 0);
  const totalLost = companySeries.reduce((a, r) => a + (r.lostTime || 0), 0);
  const totalFatalities = companySeries.reduce((a, r) => a + (r.fatalities || 0), 0);
  const totalHours = companySeries.reduce((a, r) => a + (r.hours || 0), 0);
  const totalAvgEmployees = (() => {
    const valid = companySeries.filter((r) => r.employees != null);
    if (!valid.length) return 0;
    return Math.round(
      valid.reduce((a, r) => a + r.employees, 0) / valid.length,
    );
  })();
  const latestEmr = EMR_HISTORY[EMR_HISTORY.length - 1];
  const previousEmr = EMR_HISTORY[EMR_HISTORY.length - 2];
  const emrTrend =
    previousEmr && latestEmr
      ? +(latestEmr.rate - previousEmr.rate).toFixed(2)
      : 0;
  const emrAvg =
    EMR_HISTORY.reduce((a, r) => a + r.rate, 0) / EMR_HISTORY.length;

  const activeMetricCfg = METRICS.find((m) => m.key === activeMetric);

  const renderCompanyCard = (k, opts = {}) => {
    const c = COMPANY_STATS[k];
    const totals = c.series.reduce(
      (acc, r) => {
        acc.recordable += r.recordable || 0;
        acc.lostTime += r.lostTime || 0;
        acc.fatalities += r.fatalities || 0;
        acc.hours += r.hours || 0;
        return acc;
      },
      { recordable: 0, lostTime: 0, fatalities: 0, hours: 0 },
    );
    return (
      <Card
        key={k}
        className={`${opts.span || "lg:col-span-2"} ${
          opts.highlighted
            ? "ring-2 ring-offset-2"
            : ""
        }`}
        {...(opts.highlighted ? { style: { boxShadow: `0 0 0 2px ${c.color}33` } } : {})}
      >
        <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
          <SectionTitle
            title={
              opts.highlighted ? `${c.label} — Selected` : c.label
            }
            subtitle={c.note || `${c.series.length} years on record`}
          />
          <div className="flex gap-2 text-[11px]">
            <span className="px-2 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
              Recordable: {totals.recordable}
            </span>
            <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-100">
              Lost-Time: {totals.lostTime}
            </span>
            <span className="px-2 py-1 rounded-full bg-purple-50 text-purple-700 font-semibold border border-purple-100">
              Hours: {Math.round(totals.hours).toLocaleString()}
            </span>
          </div>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[...c.series].reverse()}
              margin={{ left: 10, right: 20, top: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip
                formatter={(v) =>
                  typeof v === "number" ? v.toLocaleString() : v
                }
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="recordable" name="Recordable" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lostTime" name="Lost Time" fill="#DC2626" radius={[4, 4, 0, 0]} />
              <Bar dataKey="fatalities" name="Fatalities" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    );
  };

  return (
    <SideNavLayout>
      <div
        className="flex flex-col min-h-[90vh] max-h-[90vh] p-2 overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="px-2 mb-2 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-inter font-bold text-[24px] text-left">HSE Performance Stats</h1>
            <p className="text-sm text-gray-500 text-left">
              Safety experience and performance across NTACT companies (sourced from the company HSE statistics workbook).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Company
            </label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-3 py-1.5 text-sm border-2 border-[#E4E4E7] rounded-md bg-white text-black focus:outline-none focus:border-gray-500"
            >
              <option value="">Select company…</option>
              {COMPANY_KEYS.map((k) => (
                <option key={k} value={k}>
                  {COMPANY_STATS[k].label}
                </option>
              ))}
            </select>
            {selectedCompany && (
              <button
                onClick={() => setSelectedCompany("")}
                className="px-3 py-1.5 text-xs rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 px-2">
          {/* KPI tiles */}
          <KpiTile
            label="Latest EMR"
            value={latestEmr.rate.toFixed(2)}
            color="#10B981"
            hint={
              emrTrend === 0
                ? "Flat vs prior year"
                : emrTrend < 0
                ? `↓ ${Math.abs(emrTrend).toFixed(2)} vs ${previousEmr.year}`
                : `↑ ${emrTrend.toFixed(2)} vs ${previousEmr.year}`
            }
          />
          <KpiTile
            label={`Total Hours (${companySeries[companySeries.length - 1].year}–${companySeries[0].year})`}
            value={Math.round(totalHours).toLocaleString()}
            color="#9333EA"
            hint={selectedCompany ? selectedCompanyCfg.label : "All Companies (combined)"}
          />
          <KpiTile
            label="Total Recordables (career)"
            value={totalRecordable}
            color="#F59E0B"
            hint={`${selectedCompany ? selectedCompanyCfg.label : "All Companies"} · ${companySeries.length} years`}
          />
          <KpiTile
            label="Fatalities (career)"
            value={totalFatalities}
            color="#DC2626"
            hint={`${selectedCompany ? selectedCompanyCfg.label : "All Companies"} · Lost-time: ${totalLost} · Avg HC: ${totalAvgEmployees}`}
          />

          {/* Selected company's detail card — only when one is picked */}
          {selectedCompany &&
            renderCompanyCard(selectedCompany, {
              span: "lg:col-span-4",
              highlighted: true,
            })}

          {/* EMR trend chart */}
          <Card className="lg:col-span-4">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
              <SectionTitle
                title="Experience Modification Rate (EMR)"
                subtitle="Insurance benchmark · 1.00 = industry average · lower is better"
              />
              <div className="flex gap-2 text-[11px]">
                <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
                  Latest {latestEmr.year}: {latestEmr.rate.toFixed(2)}
                </span>
                <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold border border-indigo-100">
                  Avg {emrAvg.toFixed(2)}
                </span>
              </div>
            </div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={EMR_HISTORY}
                  margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                >
                  <defs>
                    <linearGradient id="emrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    fontSize={11}
                    label={{ value: "Year", position: "insideBottom", offset: -8, fontSize: 11, fill: "#6B7280" }}
                  />
                  <YAxis
                    fontSize={11}
                    domain={[0, 1.2]}
                    label={{ value: "EMR", angle: -90, position: "insideLeft", offset: 10, fontSize: 11, fill: "#6B7280" }}
                  />
                  <Tooltip />
                  <ReferenceLine y={1} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "Industry Avg (1.00)", fontSize: 10, fill: "#DC2626", position: "right" }} />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#emrGrad)"
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Metric switcher */}
          <Card className="lg:col-span-4">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
              <SectionTitle
                title={`Per-Company — ${activeMetricCfg.label}`}
                subtitle={activeMetricCfg.subtitle}
              />
              <div className="flex flex-wrap gap-2">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setActiveMetric(m.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      activeMetric === m.key
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                {activeMetric === "trir" ? (
                  <LineChart
                    data={metricData}
                    margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {COMPANY_KEYS.map((k) => (
                      <Line
                        key={k}
                        type="monotone"
                        dataKey={k}
                        name={COMPANY_STATS[k].label}
                        stroke={COMPANY_STATS[k].color}
                        strokeWidth={2}
                        connectNulls
                        dot={{ r: 3 }}
                      />
                    ))}
                  </LineChart>
                ) : (
                  <BarChart
                    data={metricData}
                    margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip
                      formatter={(v) =>
                        typeof v === "number" ? v.toLocaleString() : v
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {COMPANY_KEYS.map((k) => (
                      <Bar
                        key={k}
                        dataKey={k}
                        name={COMPANY_STATS[k].label}
                        fill={COMPANY_STATS[k].color}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Per-Company breakdown — skip the one already shown above (if any) */}
          {COMPANY_KEYS.filter(
            (k) => !selectedCompany || k !== selectedCompany,
          ).map((k) => renderCompanyCard(k))}
        </div>
      </div>
    </SideNavLayout>
  );
};

export default HseStatsDashboard;
